/** Size rules for the floating chat window, kept apart from the component so they can be tested on their own. */

import { VisibleArea, fitVertically } from '../../shared/directives/visible-area';

export interface ChatWindowSize {
  width: number;
  height: number;
}

/** Small enough to sit in a corner, big enough to read one conversation. */
export const MIN_SIZE: ChatWindowSize = { width: 320, height: 360 };
/** 280 for the conversation list plus room for a readable conversation beside it. */
export const TWO_PANE_MIN_WIDTH = 640;
/** At or below this viewport width the chat window is the whole screen. */
export const PHONE_PX = 768;
/** Viewport width from which the window opens with both panes. */
const WIDE_VIEWPORT = 1100;
/** Space kept clear around the window, so it never covers the whole screen. */
const MARGIN = 48;

export function defaultSize(viewportWidth: number, viewportHeight: number): ChatWindowSize {
  const size = viewportWidth >= WIDE_VIEWPORT ? { width: 860, height: 640 } : { width: 380, height: 560 };
  return { width: size.width, height: Math.min(size.height, viewportHeight - MARGIN) };
}

export function showsTwoPanes(width: number): boolean {
  return width >= TWO_PANE_MIN_WIDTH;
}

export function clampSize(size: ChatWindowSize, viewportWidth: number, viewportHeight: number): ChatWindowSize {
  const clamp = (value: number, min: number, max: number) => Math.round(Math.max(min, Math.min(value, Math.max(min, max))));
  return {
    width: clamp(size.width, MIN_SIZE.width, viewportWidth - MARGIN),
    height: clamp(size.height, MIN_SIZE.height, viewportHeight - MARGIN),
  };
}

/** The window's size and where its bottom-right corner sits, as distances from the screen's right and bottom. */
export interface ChatWindowBox extends ChatWindowSize {
  right: number;
  bottom: number;
}

/** An edge or corner of the window, by compass point. */
export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/**
 * The window after dragging one of its edges or corners by (dx, dy). The opposite edge stays where it is; the window
 * never shrinks below MIN_SIZE and never grows past the edge of the screen.
 */
export function resizeWindow(start: ChatWindowBox, edge: ResizeEdge, dx: number, dy: number, viewportWidth: number, viewportHeight: number): ChatWindowBox {
  const box = { ...start };
  const clamp = (value: number, min: number, max: number) => Math.round(Math.max(min, Math.min(value, max)));
  if (edge.includes('w')) {
    box.width = clamp(start.width - dx, MIN_SIZE.width, viewportWidth - start.right);
  } else if (edge.includes('e')) {
    box.width = clamp(start.width + dx, MIN_SIZE.width, start.width + start.right);
    box.right = start.right - (box.width - start.width);
  }
  if (edge.includes('n')) {
    box.height = clamp(start.height - dy, MIN_SIZE.height, viewportHeight - start.bottom);
  } else if (edge.includes('s')) {
    box.height = clamp(start.height + dy, MIN_SIZE.height, start.height + start.bottom);
    box.bottom = start.bottom - (box.height - start.height);
  }
  return box;
}

/**
 * Where the window is drawn while a soft keyboard covers the foot of the screen: raised, and shortened if it must be,
 * so the composer stays above the keyboard. Its own size and corner are kept for when the keyboard closes.
 */
export function shownAboveKeyboard(box: { height: number; bottom: number }, viewportHeight: number, visible: VisibleArea): { height: number; bottom: number } {
  const fitted = fitVertically({ top: viewportHeight - box.bottom - box.height, height: box.height }, visible, 8);
  return { height: fitted.height, bottom: viewportHeight - fitted.top - fitted.height };
}
